import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { fmt, rangeLabel } from "../../pages/videos/scenes/sceneModel";
import { frameSprite, momentFrameTime } from "../../pages/videos/scenes/sceneThumbs";
import TileImg from "../../pages/videos/scenes/TileImg";
import { highlightParts, primaryOf, resultTags, spanParts } from "./nlqModel";

// text with the matched spans marked when given, else the words starting with any of terms
export function Marked({ text, terms, spans }) {
  const parts = spans ? spanParts(text, spans) : highlightParts(text, terms);
  return parts.map((p, i) => (p.hit ? <mark key={i}>{p.t}</mark> : <React.Fragment key={i}>{p.t}</React.Fragment>));
}

export const TAG_LABELS = { speech: "speech", moment: "moment", person: "person", related: "related" };

// A frame of the video at time, loaded when it scrolls into view: its tile of the project's frame sprites, else (videos
// processed before the sprites) the video itself seeked there, else the video's thumbnail. children go over it.
export function FrameThumb({ folder, src, time, frameTime, fallback, className, children }) {
  const box = useRef(null);
  const [visible, setVisible] = useState(false);
  const [sprite, setSprite] = useState(undefined); // undefined while unknown, null when there is none
  const [failed, setFailed] = useState(false);
  const [noFallback, setNoFallback] = useState(false);

  useEffect(() => {
    if (!visible) return;
    let live = true;
    frameSprite(folder, frameTime).then((tile) => live && setSprite(tile));
    return () => {
      live = false;
    };
  }, [visible, folder, frameTime]);

  useEffect(() => {
    if (!window.IntersectionObserver) {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(box.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={box} className={"sr-thumb" + (className ? " " + className : "")} aria-hidden="true">
      {sprite && <TileImg thumb={sprite} />}
      {sprite === null && !failed && <video src={src + "#t=" + time} preload="metadata" muted playsInline onError={() => setFailed(true)} />}
      {sprite === null && failed && !noFallback && <img src={fallback} alt="" onError={() => setNoFallback(true)} />}
      {children}
      <span className="sr-thumb-time">{fmt(time)}</span>
    </div>
  );
}

export function Tag({ kind }) {
  const { t } = useTranslation();
  return <span className={"sr-tag" + (kind === "related" ? " is-related" : "")}>{t(TAG_LABELS[kind])}</span>;
}

function Row({ r, selected, hovered, onSelect, onHover, highlight }) {
  const { t } = useTranslation();
  const p = primaryOf(r);
  return (
    <button
      type="button"
      className="sr-row"
      aria-current={selected || undefined}
      data-hover={hovered || undefined}
      onClick={() => onSelect(r.id)}
      onMouseEnter={() => onHover(r.id)}
      onMouseLeave={() => onHover(null)}
      onFocus={() => onHover(r.id)}
      onBlur={() => onHover(null)}
    >
      <span className="sr-time">{fmt(r.start)}</span>
      <span className="sr-tags">
        {resultTags(r).map((k) => (
          <Tag key={k} kind={k} />
        ))}
        {r.label !== "match" && <Tag kind="related" />}
      </span>
      <span className="sr-text">
        {p.kind === "screen" ? (
          <>
            <b className="sr-speaker">{p.names.join(" & ")}</b>
            {t("on screen for {{duration}}", { duration: fmt(p.duration) })}
          </>
        ) : p.kind === "line" ? (
          <>
            {p.line.speaker && <b className="sr-speaker">{t("{{name}}:", { name: p.line.speaker, nsSeparator: false })}</b>}“
            <Marked text={p.line.text} terms={highlight.speech} spans={p.line.matches} />”
          </>
        ) : (
          <i>
            <Marked text={p.event?.text} terms={highlight.visual} />
          </i>
        )}
      </span>
    </button>
  );
}

// Results grouped by video, then scene, one row per moment
export default function ResultGroups({ groups, videos, selected, hovered, onSelect, onHover, highlight, folderOf, videoSrc, thumbSrc }) {
  const { t } = useTranslation();
  return (
    <div className="sr-groups">
      {groups.map((g) => {
        const video = videos[g.projectId] || {};
        const scenes = g.scenes.filter((s) => s.scene.index >= 0).length;
        const moments = g.scenes.reduce((n, s) => n + s.rows.length, 0);
        return (
          <section key={g.projectId} className="sr-video" aria-label={video.project_name}>
            <header className="sr-video-head">
              <Link className="sr-video-name" to={"/project/" + g.projectId + "/overview"} title={video.project_name}>
                {video.project_name}
              </Link>
              <span className="sr-video-sub">
                {scenes === 0
                  ? moments === 1
                    ? t("1 moment")
                    : t("{{count}} moments", { count: moments })
                  : scenes === 1
                    ? t("1 scene")
                    : t("{{count}} scenes", { count: scenes })}
              </span>
            </header>
            {g.scenes.map(({ scene, rows }) => (
              <div key={scene.index} className="sr-scene">
                {/* moments in a part of the video without indexed scenes have no scene header */}
                {scene.index >= 0 && (
                  <div className="sr-scene-head">
                    <FrameThumb
                      folder={folderOf(g.projectId)}
                      src={videoSrc(g.projectId)}
                      time={rows[0].start}
                      frameTime={momentFrameTime(rows[0].start, rows[0].end)}
                      fallback={thumbSrc(g.projectId)}
                    />
                    <div className="sr-scene-info">
                      <span className="sr-scene-range">
                        {t("Scene")} {scene.index + 1} · {rangeLabel(scene.start, scene.end)}
                      </span>
                      {scene.title && <span className="sr-scene-title">{scene.title}</span>}
                    </div>
                  </div>
                )}
                {rows.map((r) => (
                  <Row
                    key={r.id}
                    r={r}
                    selected={r.id === selected}
                    hovered={r.id === hovered}
                    onSelect={onSelect}
                    onHover={onHover}
                    highlight={highlight}
                  />
                ))}
              </div>
            ))}
          </section>
        );
      })}
    </div>
  );
}
